// Umsatzsteuer-Voranmeldung (UStVA) der Parallelbuchhaltung: Berechnung je Monat/Quartal, Nachvollziehbarkeit bis zum Beleg, Vergleich mit der Kanzlei, Druckvorschau,
// Export und ELSTER-Datei. INTERN / VORLAEUFIG - nichts wird an ELSTER, das Finanzamt, DATEV oder den Steuerberater uebermittelt (die ERiC-Uebermittlung ist gesperrt).
import { useState, useEffect, useCallback, useMemo } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { pbApi, API } from "../../../belege/belegeApi";
import { Meldung, Chip, Karte, Klein, Modal, Bestaetigung, useAktion } from "../../../belege/ui";
import { Seitenkopf, PbBanner, Abschnitt, Kachel, kachelRaster, geld, useBreite, BREAKPOINT_KLEIN } from "../../ui/Bausteine";

const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
export const ZEITRAEUME = [
  ...MONATE.map((m, i) => [String(i + 1).padStart(2, "0"), `${m} (Monat ${String(i + 1).padStart(2, "0")})`]),
  ["41", "1. Quartal (41)"], ["42", "2. Quartal (42)"], ["43", "3. Quartal (43)"], ["44", "4. Quartal (44)"],
];
const STUFE_FARBE = { blocker: THEME.colors.danger, warnung: THEME.colors.warning, hinweis: THEME.colors.textSecondary };
const STUFE_NAME = { blocker: "BLOCKER", warnung: "Warnung", hinweis: "Hinweis" };
const zahl = (n) => (n === null || n === undefined ? "" : Number(n).toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 }));
const standardZeitraum = () => { const h = new Date(); const m = h.getMonth() === 0 ? 12 : h.getMonth(); return { jahr: h.getMonth() === 0 ? h.getFullYear() - 1 : h.getFullYear(), zeitraum: String(m).padStart(2, "0") }; };

function useZeitraum() {
  const [wahl, setWahl] = useState(standardZeitraum);
  return [wahl, setWahl];
}

function ZeitraumLeiste({ wahl, setWahl, jahre, rechts }) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end", marginBottom: 14 }}>
      <label><span style={fs.label}>Jahr</span>
        <select style={fs.input} value={wahl.jahr} onChange={e => setWahl({ ...wahl, jahr: Number(e.target.value) })}>
          {(jahre || [2026]).map(j => <option key={j} value={j}>{j}</option>)}
        </select></label>
      <label><span style={fs.label}>Zeitraum</span>
        <select style={{ ...fs.input, minWidth: 220 }} value={wahl.zeitraum} onChange={e => setWahl({ ...wahl, zeitraum: e.target.value })}>
          {ZEITRAEUME.map(([w, t]) => <option key={w} value={w}>{t}</option>)}
        </select></label>
      {rechts}
    </div>
  );
}

function useUstva(wahl, setFehler) {
  const [erg, setErg] = useState(null);
  const [laed, setLaed] = useState(false);
  const laden = useCallback(() => {
    setLaed(true);
    return pbApi.ustvaBerechnung(wahl.jahr, wahl.zeitraum).then(e => { setErg(e); setFehler(""); }).catch(e => { setErg(null); setFehler(e.message); }).finally(() => setLaed(false));
  }, [wahl.jahr, wahl.zeitraum, setFehler]);
  useEffect(() => { laden(); }, [laden]);
  return { erg, laed, laden };
}

function Freigabe({ erg }) {
  const ok = erg.freigabefaehig;
  const f = ok ? THEME.colors.success : THEME.colors.danger;
  return (
    <div style={{ border: `2px solid ${f}`, borderRadius: THEME.radius.lg, padding: "10px 14px", marginBottom: 14, background: THEME.colors.surface }}>
      <strong style={{ color: f }}>{ok ? "Technisch freigabefähig – keine bekannten offenen Punkte" : "NICHT freigabefähig – wesentliche offene Punkte"}</strong>
      <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 4 }}>{erg.hinweis}</div>
      <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 4 }}>
        Methode: {erg.methode ? (erg.methode === "ist" ? "Istversteuerung" : "Sollversteuerung") : "nicht festgelegt"}
        {erg.methode_status?.freigegeben ? ` · vom Steuerberater bestätigt (${erg.methode_status.bestaetigt_von || "?"})` : " · vom Steuerberater NICHT bestätigt"}
        {" · "}{erg.buchungen_im_zeitraum} Buchungen im Zeitraum · Vordruck: {erg.vordruck}
      </div>
    </div>
  );
}

function WarnZeile({ w }) {
  const [offen, setOffen] = useState(false);
  const d = w.details || [];
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <span style={{ flex: "0 0 auto" }}><Chip text={STUFE_NAME[w.stufe] || w.stufe} farbe={STUFE_FARBE[w.stufe]} /></span>
        <span style={{ color: THEME.colors.text, overflowWrap: "anywhere" }}>{w.text}
          {d.length > 0 && <button style={{ ...fs.btnGray, padding: "2px 8px", marginLeft: 8, fontSize: THEME.fontSize.sm }} onClick={() => setOffen(!offen)}>{offen ? "Einzelpositionen ausblenden" : `Einzelpositionen (${d.length})`}</button>}
        </span>
      </div>
      {offen && (
        <div style={{ margin: "6px 0 0 24px", maxHeight: 240, overflow: "auto", fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>
          {d.map((x, i) => <div key={i}>Buchung #{x.buchung_id} · {x.bezeichnung || "–"} · {geld(x.betrag)}{x.einnahme === true ? " (Einnahme)" : x.einnahme === false ? " (Ausgabe)" : ""}</div>)}
        </div>
      )}
    </div>
  );
}

function Warnungen({ erg }) {
  const liste = [...erg.warnungen].sort((a, b) => ["blocker", "warnung", "hinweis"].indexOf(a.stufe) - ["blocker", "warnung", "hinweis"].indexOf(b.stufe));
  if (!liste.length) return null;
  return (
    <Abschnitt titel={`Warnungen und Hinweise (${erg.blocker.length} Blocker)`}>
      {liste.map((w, i) => <WarnZeile key={i} w={w} />)}
    </Abschnitt>
  );
}

function Detail({ wahl, kz, text, onSchliessen }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState("");
  useEffect(() => { pbApi.ustvaDetail(wahl.jahr, wahl.zeitraum, kz).then(setD).catch(e => setFehler(e.message)); }, [wahl.jahr, wahl.zeitraum, kz]);
  return (
    <Modal titel={`Kennzahl ${kz}`} onSchliessen={onSchliessen} breite={960}>
      <div style={{ color: THEME.colors.text }}>
        <strong>Kz {kz}</strong> – {text}
        <Meldung fehler={fehler} />
        {!d && !fehler && <Klein>Lädt…</Klein>}
        {d && (
          <>
            <Klein>{d.hinweis}</Klein>
            {d.bestandteile_83 && (
              <Klein>Summe Umsatzsteuer {geld(d.bestandteile_83.umsatzsteuer_summe)} − Summe Vorsteuer {geld(d.bestandteile_83.vorsteuer_summe)} = {geld(d.bestandteile_83.verbleibender_betrag)}</Klein>
            )}
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: THEME.fontSize.sm }}>
                <thead><tr>{["Datum", "Art", "Betrag", "Text", "Leistung", "Sicherheit", "Beleg / Quelle"].map(h => <th key={h} style={{ ...fs.th, padding: "6px 8px" }}>{h}</th>)}</tr></thead>
                <tbody>
                  {d.positionen.map((p, i) => (
                    <tr key={i}>
                      <td style={{ ...fs.td, padding: "6px 8px" }}>{p.datum ? new Date(p.datum).toLocaleDateString("de-DE") : "–"}</td>
                      <td style={{ ...fs.td, padding: "6px 8px" }}>{p.art === "bmg" ? "Bemessungsgr." : "Steuer"}</td>
                      <td style={{ ...fs.td, padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{geld(p.betrag)}</td>
                      <td style={{ ...fs.td, padding: "6px 8px", overflowWrap: "anywhere" }}>{p.text}{p.hinweis ? <Klein>{p.hinweis}</Klein> : null}</td>
                      <td style={{ ...fs.td, padding: "6px 8px" }}>{p.leistungsart}</td>
                      <td style={{ ...fs.td, padding: "6px 8px" }}><Chip text={p.sicherheit} farbe={p.sicherheit === "unsicher" ? THEME.colors.danger : p.sicherheit === "sicher" ? THEME.colors.success : THEME.colors.warning} /></td>
                      <td style={{ ...fs.td, padding: "6px 8px", overflowWrap: "anywhere" }}>
                        {p.beleg_datei ? <a href={`${API}${p.beleg_datei}`} target="_blank" rel="noreferrer" style={{ color: THEME.colors.primary }}>{p.beleg || `Beleg ${p.beleg_id}`}{p.lieferant ? ` (${p.lieferant})` : ""}</a> : <span style={{ color: THEME.colors.textMuted }}>kein Beleg</span>}
                        <Klein>{p.quelle === "manuell" ? "manuell erfasst" : p.quelle === "rechnung" ? `Ausgangsrechnung #${p.rechnung_id}` : `Buchung #${p.buchung_id}${p.bankumsatz_id ? ` · Bankumsatz #${p.bankumsatz_id}` : ""}`}</Klein>
                      </td>
                    </tr>
                  ))}
                  {d.positionen.length === 0 && <tr><td colSpan={7} style={{ ...fs.td, color: THEME.colors.textMuted }}>Keine Einzelpositionen.</td></tr>}
                </tbody>
              </table>
            </div>
            <Klein>Summe Bemessungsgrundlage {geld(d.summe_bmg)} · Summe Steuer {geld(d.summe_steuer)}</Klein>
          </>
        )}
      </div>
    </Modal>
  );
}

function KennzahlTabelle({ erg, wahl }) {
  const breite = useBreite();
  const [offen, setOffen] = useState(null);
  const werte = Object.values(erg.kennzahlen).filter(k => k.kz && !k.kz.startsWith("_"));
  const abschnitte = [...new Set(werte.map(k => k.abschnitt))].sort();
  const klein = breite < BREAKPOINT_KLEIN;
  if (werte.length <= 1 && Number(erg.kennzahlen["83"]?.steuer) === 0) {
    return <Abschnitt titel="Kennzahlen"><Klein>In diesem Zeitraum ergeben sich keine Werte (keine Buchungen mit Umsatzsteuer-Bezug).</Klein></Abschnitt>;
  }
  return (
    <Abschnitt titel="Kennzahlen nach amtlichem Vordruck (Klick auf „Buchungen“ zeigt die Einzelbuchungen und Belege)">
      {abschnitte.map(ab => (
        <div key={ab} style={{ marginBottom: 14 }}>
          <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, textTransform: "uppercase", marginBottom: 4 }}>Abschnitt {ab}</div>
          {klein ? werte.filter(k => k.abschnitt === ab).sort((a, b) => a.zeile - b.zeile).map(k => (
            <div key={k.kz} style={{ ...fs.karte, padding: "10px 12px", marginBottom: 8 }}>
              <div><strong>Kz {k.kz}</strong> <span style={{ color: THEME.colors.textMuted }}>· Zeile {k.zeile}</span></div>
              <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{k.text}</div>
              <div style={{ marginTop: 4 }}>{k.bmg !== null && <span>Bemessungsgrundlage {zahl(k.bmg)} € </span>}{k.steuer !== null && <span>· Steuer {geld(k.steuer)}</span>}</div>
              <button style={{ ...fs.btnGray, padding: "6px 12px", marginTop: 6 }} onClick={() => setOffen(k)}>Buchungen</button>
            </div>
          )) : (
            <div style={{ ...fs.tabelle }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={fs.th}>Kz</th><th style={fs.th}>Zeile</th><th style={fs.th}>Bezeichnung</th><th style={{ ...fs.th, textAlign: "right" }}>Bemessungsgrundlage</th><th style={{ ...fs.th, textAlign: "right" }}>Steuer</th><th style={fs.th}></th></tr></thead>
                <tbody>
                  {werte.filter(k => k.abschnitt === ab).sort((a, b) => a.zeile - b.zeile).map(k => (
                    <tr key={k.kz}>
                      <td style={{ ...fs.td, fontWeight: 700 }}>{k.kz}</td>
                      <td style={fs.td}>{k.zeile}</td>
                      <td style={{ ...fs.td, overflowWrap: "anywhere" }}>{k.text}</td>
                      <td style={{ ...fs.td, textAlign: "right", whiteSpace: "nowrap" }}>{k.bmg !== null ? `${zahl(k.bmg)} €` : ""}</td>
                      <td style={{ ...fs.td, textAlign: "right", whiteSpace: "nowrap", fontWeight: k.kz === "83" ? 700 : 400 }}>{k.steuer !== null ? geld(k.steuer) : ""}</td>
                      <td style={fs.td}><button style={{ ...fs.btnGray, padding: "4px 10px" }} onClick={() => setOffen(k)}>Buchungen</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
      {offen && <Detail wahl={wahl} kz={offen.kz} text={offen.text} onSchliessen={() => setOffen(null)} />}
    </Abschnitt>
  );
}

// ------------------------------------------------------------------ Druckvorschau (hell, nur dieser Block wird gedruckt)
function Druckvorschau({ erg, onSchliessen }) {
  const werte = Object.values(erg.kennzahlen).filter(k => k.kz && !k.kz.startsWith("_")).sort((a, b) => a.zeile - b.zeile);
  const s = erg.kennzahlen._summen;
  const td = { border: "1px solid #999", padding: "3px 6px", fontSize: 12, color: "#000" };
  return (
    <div>
      <style>{`@media print{body *{visibility:hidden !important}.ustva-druck,.ustva-druck *{visibility:visible !important}.ustva-druck{position:absolute;left:0;top:0;width:100%}.ustva-keinen-druck{display:none !important}}`}</style>
      <div className="ustva-keinen-druck" style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button style={fs.btn} onClick={() => window.print()}>Drucken / als PDF speichern</button>
        <button style={fs.btnGray} onClick={onSchliessen}>Druckvorschau schließen</button>
      </div>
      <div className="ustva-druck" style={{ background: "#fff", color: "#000", padding: 20, borderRadius: 8, maxWidth: 900 }}>
        <div style={{ border: "2px solid #b00", color: "#b00", padding: "6px 10px", fontWeight: 700, marginBottom: 10 }}>
          INTERN – VORLÄUFIG – KEINE amtliche Umsatzsteuer-Voranmeldung. Nichts wurde an das Finanzamt übermittelt.
        </div>
        <h2 style={{ margin: "0 0 4px", color: "#000" }}>Umsatzsteuer-Voranmeldung {erg.jahr} – {erg.zeitraum_name}</h2>
        <div style={{ fontSize: 12, marginBottom: 8 }}>Zeitraum {new Date(erg.von).toLocaleDateString("de-DE")} – {new Date(erg.bis).toLocaleDateString("de-DE")} · Methode: {erg.methode === "ist" ? "Istversteuerung" : erg.methode === "soll" ? "Sollversteuerung" : "nicht festgelegt"}
          {" "}({erg.methode_status?.freigegeben ? "vom Steuerberater bestätigt" : "vom Steuerberater NICHT bestätigt"}) · Vordruck: {erg.vordruck}</div>
        <div style={{ fontWeight: 700, color: erg.freigabefaehig ? "#060" : "#b00", marginBottom: 6 }}>
          {erg.freigabefaehig ? "Technisch freigabefähig (keine Bestätigung der Richtigkeit)" : "NICHT freigabefähig"}
        </div>
        <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
          <thead><tr>{["Kz", "Zeile", "Bezeichnung", "Bemessungsgrundlage (€)", "Steuer (€)"].map(h => <th key={h} style={{ ...td, background: "#eee", textAlign: "left" }}>{h}</th>)}</tr></thead>
          <tbody>
            {werte.map(k => (
              <tr key={k.kz}><td style={td}>{k.kz}</td><td style={td}>{k.zeile}</td><td style={td}>{k.text}</td>
                <td style={{ ...td, textAlign: "right" }}>{k.bmg !== null ? zahl(k.bmg) : ""}</td><td style={{ ...td, textAlign: "right" }}>{k.steuer !== null ? Number(k.steuer).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : ""}</td></tr>
            ))}
          </tbody>
        </table></div>
        <div style={{ fontSize: 12 }}>Summe Umsatzsteuer: <strong>{geld(s.umsatzsteuer_summe)}</strong> · Abziehbare Vorsteuer: <strong>{geld(s.vorsteuer_summe)}</strong> · Verbleibender Betrag: <strong>{geld(s.verbleibender_betrag)}</strong></div>
        {erg.warnungen.filter(w => w.stufe !== "hinweis").length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12 }}><strong>Offene Punkte:</strong>
            <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>{erg.warnungen.filter(w => w.stufe !== "hinweis").map((w, i) => <li key={i}>{STUFE_NAME[w.stufe]}: {w.text}</li>)}</ul>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 11, color: "#444" }}>Erstellt am {new Date().toLocaleString("de-DE")} · ComeFit Parallelbuchhaltung (intern). Maßgeblich bleibt die Buchhaltung des Steuerberaters.</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Export / ELSTER
function speichereDatei(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

function ExportBereich({ wahl, erg, st, setFehler, setHinweis }) {
  const aktion = useAktion(setFehler, setHinweis);
  const [absender, setAbsender] = useState(() => { try { return JSON.parse(localStorage.getItem("pb_ustva_absender") || "{}"); } catch (e) { return {}; } });
  const [entwurf, setEntwurf] = useState(false);
  const [xml, setXml] = useState(null);
  const [eric, setEric] = useState(null);
  useEffect(() => { pbApi.ustvaEric().then(setEric).catch(() => {}); }, []);
  const setA = (k, v) => { const n = { ...absender, [k]: v }; setAbsender(n); try { localStorage.setItem("pb_ustva_absender", JSON.stringify(n)); } catch (e) { /* ohne Speicher weiter */ } };
  const lade = (format) => aktion(async () => { const b = await pbApi.ustvaDownload(wahl.jahr, wahl.zeitraum, format); speichereDatei(b, `UStVA_${wahl.jahr}_${wahl.zeitraum}.${format}`); }, "Datei erstellt.");
  const erzeuge = () => aktion(async () => { setXml(await pbApi.ustvaXml({ jahr: wahl.jahr, zeitraum: wahl.zeitraum, name: absender.name || "", strasse: absender.strasse || "", plz: absender.plz || "", ort: absender.ort || "", entwurf })); }, "ELSTER-Datei erzeugt (nichts übermittelt).");
  const pr = xml?.pruefung;
  return (
    <>
      <Abschnitt titel="Export der Kennzahlen">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={fs.btnGray} onClick={() => lade("csv")}>CSV (Excel)</button>
          <button style={fs.btnGray} onClick={() => lade("json")}>JSON</button>
        </div>
        <Klein>Beide Dateien tragen den Vermerk „intern / vorläufig“ und den Freigabestatus.</Klein>
      </Abschnitt>
      <Abschnitt titel="ELSTER-Datei (Formulardaten-Upload) – wird nur erzeugt, nicht übermittelt">
        <Klein>Die Steuernummer (Format 10–13 Ziffern) steht in den Einstellungen unten. Das Hochladen im ELSTER-Portal ist ein eigener, bewusster Schritt nach Prüfung durch den Steuerberater.</Klein>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, margin: "10px 0" }}>
          {[["name", "Name / Firma"], ["strasse", "Straße und Hausnummer"], ["plz", "PLZ"], ["ort", "Ort"]].map(([k, t]) => (
            <label key={k}><span style={fs.label}>{t}</span><input style={{ ...fs.input, width: "100%" }} value={absender[k] || ""} onChange={e => setA(k, e.target.value)} /></label>
          ))}
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <input type="checkbox" checked={entwurf} onChange={e => setEntwurf(e.target.checked)} />
          <span>Als ENTWURF erzeugen (auch wenn nicht freigabefähig – Datei wird deutlich als „NICHT EINREICHEN“ gekennzeichnet)</span>
        </label>
        <button style={fs.btn} onClick={erzeuge} disabled={!erg.freigabefaehig && !entwurf} title={!erg.freigabefaehig && !entwurf ? "Nicht freigabefähig – nur als Entwurf möglich" : ""}>ELSTER-XML erzeugen</button>
        {!erg.freigabefaehig && !entwurf && <Klein farbe={THEME.colors.warning}>Eine einreichbare Datei gibt es nur, wenn die UStVA freigabefähig ist.</Klein>}
        {xml && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <Chip text={xml.entwurf ? "ENTWURF – nicht einreichen" : "freigabefähig"} farbe={xml.entwurf ? THEME.colors.danger : THEME.colors.success} />
              <Chip text={pr.status === "validiert" ? "gegen amtliches Schema validiert" : pr.status === "fehlerhaft" ? "Schema-Prüfung: FEHLER" : "amtliches Schema NICHT geprüft"} farbe={pr.status === "validiert" ? THEME.colors.success : pr.status === "fehlerhaft" ? THEME.colors.danger : THEME.colors.warning} />
              <button style={fs.btnGray} onClick={() => speichereDatei(new Blob([xml.xml], { type: "application/xml" }), xml.dateiname)}>{xml.dateiname} herunterladen</button>
            </div>
            {pr.grund && <Klein farbe={THEME.colors.warning}>{pr.grund}</Klein>}
            {(pr.fehler || []).map((f, i) => <Klein key={i} farbe={THEME.colors.danger}>{f}</Klein>)}
            <Klein>{xml.hinweis}</Klein>
            <textarea readOnly value={xml.xml} style={{ ...fs.input, width: "100%", height: 200, fontFamily: "monospace", fontSize: 12, marginTop: 8 }} />
          </div>
        )}
      </Abschnitt>
      <Abschnitt titel="Direkte ELSTER-Übermittlung (ERiC)">
        <Chip text="nicht umgesetzt · gesperrt" farbe={THEME.colors.danger} />
        <Klein>{eric?.hinweis}</Klein>
      </Abschnitt>
    </>
  );
}

// ------------------------------------------------------------------ Einstellungen: Kontenzuordnung, Methode, Schwellen
function UstvaEinstellungen({ st, neuLaden, onGeaendert, setFehler, setHinweis }) {
  const aktion = useAktion(setFehler, setHinweis);
  const [konten, setKonten] = useState([]);
  const [meth, setMeth] = useState(null);
  const [form, setForm] = useState({ methode: "ist", steuerberater: "", notiz: "" });
  const [frage, setFrage] = useState(false);
  const breite = useBreite();
  const e = st.einstellungen || {};
  const laden = useCallback(() => { pbApi.ustvaKonten().then(setKonten).catch(x => setFehler(x.message)); pbApi.ustvaMethode().then(setMeth).catch(() => {}); }, [setFehler]);
  useEffect(() => { laden(); }, [laden]);
  const fertig = () => { laden(); neuLaden(); onGeaendert(); };
  const setze = (k, v) => aktion(() => pbApi.einstellung(k, v), "Gespeichert.").then(fertig);
  const unbest = konten.filter(k => !k.bestaetigt).length;
  return (
    <>
      <Abschnitt titel="Umsatzsteuer-Methode (Ist/Soll) – erst nach Bestätigung durch den Steuerberater freigegeben">
        {meth && <div style={{ marginBottom: 8 }}><Chip text={meth.freigegeben ? `freigegeben: ${meth.bestaetigt} (bestätigt von ${meth.bestaetigt_von})` : meth.bestaetigt ? `Bestätigung (${meth.bestaetigt}) weicht von der Einstellung (${meth.eingestellt || "–"}) ab` : "NICHT bestätigt"} farbe={meth.freigegeben ? THEME.colors.success : THEME.colors.danger} /></div>}
        <Klein>Die Methode selbst wird unter „Einstellungen und Zuordnungen“ festgelegt. Hier wird nur dokumentiert, dass der Steuerberater sie bestätigt hat. Das Datum wird automatisch gesetzt.</Klein>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end", marginTop: 8 }}>
          <label><span style={fs.label}>Methode</span><select style={fs.input} value={form.methode} onChange={ev => setForm({ ...form, methode: ev.target.value })}><option value="ist">Istversteuerung</option><option value="soll">Sollversteuerung</option></select></label>
          <label><span style={fs.label}>Bestätigt durch (Name Steuerberater)</span><input style={fs.input} value={form.steuerberater} onChange={ev => setForm({ ...form, steuerberater: ev.target.value })} /></label>
          <label><span style={fs.label}>Notiz (z. B. Datum/Telefonat)</span><input style={fs.input} value={form.notiz} onChange={ev => setForm({ ...form, notiz: ev.target.value })} /></label>
          <button style={fs.btn} disabled={!form.steuerberater.trim()} onClick={() => aktion(() => pbApi.ustvaMethodeBestaetigen(form), "Bestätigung gespeichert.").then(fertig)}>Bestätigung festhalten</button>
        </div>
      </Abschnitt>
      <Abschnitt titel={`Zuordnung Konto → Kennzahl (${unbest} unbestätigt)`}>
        <Klein>Bestimmt, aus welchen SKR03-Konten die Kennzahlen entstehen. Die Vorschläge sind NICHT bestätigt, bis ausdrücklich bestätigt wird (am besten nach Abstimmung mit dem Steuerberater).</Klein>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0" }}>
          <button style={fs.btnGray} onClick={() => aktion(() => pbApi.ustvaKontenVorschlaege(), "Vorschläge angelegt.").then(fertig)}>Standard-Vorschläge anlegen</button>
          <button style={fs.btn} disabled={!konten.length || !unbest} onClick={() => setFrage(true)}>Alle bestätigen</button>
        </div>
        {breite < BREAKPOINT_KLEIN ? konten.map(k => (
          <div key={k.konto} style={{ ...fs.karte, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><strong>Konto {k.konto}</strong><Chip text={k.bestaetigt ? "bestätigt" : "unbestätigt"} farbe={k.bestaetigt ? THEME.colors.success : THEME.colors.warning} /></div>
            <div style={{ fontSize: THEME.fontSize.sm }}>{k.art} · Kz BMG {k.kz_bmg || "–"} · Kz Steuer {k.kz_steuer || "–"} · Satz {k.satz ?? "–"}</div>
            <Klein>{k.hinweis}</Klein>
          </div>
        )) : (
          <div style={{ ...fs.tabelle }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Konto", "Art", "Kz BMG", "Kz Steuer", "Satz", "Status", "Hinweis"].map(h => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
              <tbody>
                {konten.map(k => (
                  <tr key={k.konto}>
                    <td style={{ ...fs.td, fontWeight: 700 }}>{k.konto}</td><td style={fs.td}>{k.art}</td><td style={fs.td}>{k.kz_bmg || "–"}</td><td style={fs.td}>{k.kz_steuer || "–"}</td><td style={fs.td}>{k.satz ?? "–"}</td>
                    <td style={{ ...fs.td, whiteSpace: "nowrap" }}><Chip text={k.bestaetigt ? "bestätigt" : "unbestätigt"} farbe={k.bestaetigt ? THEME.colors.success : THEME.colors.warning} /></td>
                    <td style={{ ...fs.td, overflowWrap: "anywhere" }}>{k.hinweis}</td>
                  </tr>
                ))}
                {!konten.length && <tr><td colSpan={7} style={{ ...fs.td, color: THEME.colors.textMuted }}>Noch keine Zuordnung.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Abschnitt>
      <Abschnitt titel="Schwellen und ELSTER-Angaben">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          <Eingabe label="Wesentlichkeitsgrenze (EUR)" wert={e.ustva_wesentlichkeit} hilfe="Offene Punkte unterhalb der Summe blockieren nicht (0 = jeder offene Punkt blockiert)." onSpeichern={v => setze("ustva_wesentlichkeit", v)} />
          <Eingabe label="Steuernummer (ELSTER-Format, 10–13 Ziffern)" wert={e.elster_steuernummer} hilfe="Nur für die ELSTER-Datei." onSpeichern={v => setze("elster_steuernummer", v)} />
          <Eingabe label="Amtliches XSD (Datei in backend/elster_schemata)" wert={e.elster_xsd_pfad} hilfe="Ohne Schema bleibt die ELSTER-Datei „nicht geprüft“." onSpeichern={v => setze("elster_xsd_pfad", v)} />
        </div>
      </Abschnitt>
      {frage && <Bestaetigung titel="Alle Zuordnungen bestätigen?" okText="Ja, bestätigen" text="Damit gilt die Zuordnung Konto → Kennzahl als abgestimmt. Bitte nur nach Rücksprache mit dem Steuerberater." onAbbruch={() => setFrage(false)} onOk={() => { setFrage(false); aktion(() => pbApi.ustvaKontenBestaetigen(), "Zuordnungen bestätigt.").then(fertig); }} />}
    </>
  );
}

function Eingabe({ label, wert, hilfe, onSpeichern }) {
  const [v, setV] = useState(wert ?? "");
  useEffect(() => { setV(wert ?? ""); }, [wert]);
  return (
    <label><span style={fs.label}>{label}</span>
      <div style={{ display: "flex", gap: 6 }}>
        <input style={{ ...fs.input, flex: 1, minWidth: 0 }} value={v} onChange={e => setV(e.target.value)} />
        <button style={fs.btnGray} disabled={v === (wert ?? "")} onClick={() => onSpeichern(v)}>Speichern</button>
      </div>
      <Klein>{hilfe}</Klein>
    </label>
  );
}

// ------------------------------------------------------------------ Seite "Umsatzsteuer-Voranmeldung"
export function UstvaSeite() {
  const [wahl, setWahl] = useZeitraum();
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [jahre, setJahre] = useState([2026]);
  const [druck, setDruck] = useState(false);
  const [st, setSt] = useState(null);
  const aktion = useAktion(setFehler, setHinweis);
  const { erg, laed, laden } = useUstva(wahl, setFehler);
  const stLaden = useCallback(() => pbApi.status().then(setSt).catch(e => setFehler(e.message)), []);
  useEffect(() => { pbApi.ustvaKennzahlen(2026).then(k => setJahre(k.jahre)).catch(() => {}); stLaden(); }, [stLaden]);
  const s = erg?.kennzahlen?._summen;
  return (
    <div style={{ color: THEME.colors.text }}>
      <PbBanner />
      <Seitenkopf titel="Umsatzsteuer-Voranmeldung" untertitel="Aus den Buchungen, Rechnungen und Belegen ermittelt – nachvollziehbar bis zum Originalbeleg. Es wird nichts an ELSTER oder das Finanzamt übermittelt." />
      <Meldung fehler={fehler} hinweis={hinweis} />
      <ZeitraumLeiste wahl={wahl} setWahl={setWahl} jahre={jahre} rechts={<>
        <button style={fs.btnGray} onClick={laden} disabled={laed}>{laed ? "Rechnet…" : "Neu berechnen"}</button>
        <button style={fs.btnGray} disabled={!erg} onClick={() => aktion(() => pbApi.ustvaSpeichern(wahl.jahr, wahl.zeitraum), "Berechnung festgehalten (mit allen Einzelpositionen).")}>Berechnung festhalten</button>
        <button style={fs.btnGray} disabled={!erg} onClick={() => setDruck(!druck)}>Druckvorschau</button>
      </>} />
      {!erg && !fehler && <Klein>Lädt…</Klein>}
      {erg && druck && <Druckvorschau erg={erg} onSchliessen={() => setDruck(false)} />}
      {erg && !druck && (
        <>
          <Freigabe erg={erg} />
          <div style={kachelRaster}>
            <Kachel titel="Summe Umsatzsteuer" wert={geld(s.umsatzsteuer_summe)} hinweis="Zeilen 13–18 und 25–32" />
            <Kachel titel="Abziehbare Vorsteuer" wert={geld(s.vorsteuer_summe)} hinweis="Zeilen 38–44" />
            <Kachel titel="Kz 83 · Vorauszahlung / Überschuss" wert={geld(erg.kennzahlen["83"].steuer)} gross farbe={Number(erg.kennzahlen["83"].steuer) < 0 ? THEME.colors.success : THEME.colors.text} hinweis="Umsatzsteuer − Vorsteuer (Minus = Erstattung)" />
          </div>
          <Warnungen erg={erg} />
          <KennzahlTabelle erg={erg} wahl={wahl} />
        </>
      )}
      {erg && !druck && <ExportBereich wahl={wahl} erg={erg} st={st} setFehler={setFehler} setHinweis={setHinweis} />}
      {st && !druck && <UstvaEinstellungen st={st} neuLaden={stLaden} onGeaendert={laden} setFehler={setFehler} setHinweis={setHinweis} />}
    </div>
  );
}

// ------------------------------------------------------------------ Seite "UStVA-Vergleich mit dem Steuerberater"
const V_STATUS = {
  gleich: ["übereinstimmend", THEME.colors.success], abweichend: ["abweichend", THEME.colors.danger], nur_comefit: ["nur ComeFit", THEME.colors.warning],
  nur_kanzlei: ["nur Kanzlei", THEME.colors.warning], nicht_vergleichbar: ["nicht vergleichbar", THEME.colors.textMuted],
};

export function UstvaVergleichSeite() {
  const [wahl, setWahl] = useZeitraum();
  const [fehler, setFehler] = useState("");
  const [v, setV] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [detail, setDetail] = useState(null);
  const breite = useBreite();
  useEffect(() => { setV(null); pbApi.ustvaVergleich(wahl.jahr, wahl.zeitraum).then(x => { setV(x); setFehler(""); }).catch(e => setFehler(e.message)); }, [wahl.jahr, wahl.zeitraum]);
  const zeilen = useMemo(() => (v?.zeilen || []).filter(z => filter === "alle" || z.status === filter), [v, filter]);
  const klein = breite < BREAKPOINT_KLEIN;
  const z = v?.zusammenfassung;
  return (
    <div style={{ color: THEME.colors.text }}>
      <PbBanner />
      <Seitenkopf titel="UStVA-Vergleich mit dem Steuerberater" untertitel="Die berechnete ComeFit-UStVA neben den Werten aus dem UStVA-Protokoll bzw. der SuSa des Steuerberaters – Abweichung je Kennzahl." />
      <Meldung fehler={fehler} />
      <ZeitraumLeiste wahl={wahl} setWahl={setWahl} jahre={[2026]} />
      {!v && !fehler && <Klein>Lädt…</Klein>}
      {v && !v.vergleich_moeglich && <Karte titel="Kein Vergleich möglich">{v.hinweise.map((h, i) => <div key={i} style={{ color: THEME.colors.warning }}>{h}</div>)}</Karte>}
      {v && v.vergleich_moeglich && (
        <>
          <div style={kachelRaster}>
            <Kachel titel="Kz 83 ComeFit" wert={geld(z.kz83_comefit)} />
            <Kachel titel="Kz 83 Steuerberater" wert={geld(z.kz83_kanzlei)} hinweis={v.quelle === "protokoll" ? "aus UStVA-Protokoll" : v.quelle === "protokoll_felder" ? "aus gespeicherten Feldern (Rohtext fehlt)" : "aus SuSa abgeleitet"} />
            <Kachel titel="Differenz Kz 83" wert={z.kz83_differenz === null || z.kz83_differenz === undefined ? "–" : geld(z.kz83_differenz, true)} gross farbe={Number(z.kz83_differenz) === 0 ? THEME.colors.success : THEME.colors.danger} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <button style={{ ...fs.btnGray, outline: filter === "alle" ? `2px solid ${THEME.colors.primary}` : "none" }} onClick={() => setFilter("alle")}>Alle ({v.zeilen.length})</button>
            {Object.entries(V_STATUS).map(([k, [t]]) => {
              const n = v.zeilen.filter(x => x.status === k).length;
              return n > 0 && <button key={k} style={{ ...fs.btnGray, outline: filter === k ? `2px solid ${THEME.colors.primary}` : "none" }} onClick={() => setFilter(k)}>{t} ({n})</button>;
            })}
          </div>
          {v.hinweise.map((h, i) => <Klein key={i} farbe={THEME.colors.warning}>{h}</Klein>)}
          <div style={{ marginTop: 10 }}>
            {klein ? zeilen.map(r => <VergleichKarte key={r.kz} r={r} onDetail={() => setDetail(r)} />) : (
              <div style={fs.tabelle}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={fs.th}>Kz</th><th style={fs.th}>Bezeichnung</th>
                    <th style={{ ...fs.th, textAlign: "right" }}>ComeFit BMG</th><th style={{ ...fs.th, textAlign: "right" }}>Kanzlei BMG</th>
                    <th style={{ ...fs.th, textAlign: "right" }}>ComeFit Steuer</th><th style={{ ...fs.th, textAlign: "right" }}>Kanzlei Steuer</th><th style={{ ...fs.th, textAlign: "right" }}>Differenz</th>
                    <th style={fs.th}>Status</th><th style={fs.th}></th></tr></thead>
                  <tbody>
                    {zeilen.map(r => (
                      <tr key={r.kz}>
                        <td style={{ ...fs.td, fontWeight: 700 }}>{r.kz}</td>
                        <td style={{ ...fs.td, overflowWrap: "anywhere" }}>{r.text}{r.hinweis && <Klein>{r.hinweis}</Klein>}</td>
                        <td style={{ ...fs.td, textAlign: "right" }}>{r.comefit_bmg === null ? "" : zahl(r.comefit_bmg)}</td><td style={{ ...fs.td, textAlign: "right" }}>{r.kanzlei_bmg === null ? "" : zahl(r.kanzlei_bmg)}</td>
                        <td style={{ ...fs.td, textAlign: "right", whiteSpace: "nowrap" }}>{r.comefit_steuer === null ? "" : geld(r.comefit_steuer)}</td><td style={{ ...fs.td, textAlign: "right", whiteSpace: "nowrap" }}>{r.kanzlei_steuer === null ? "" : geld(r.kanzlei_steuer)}</td>
                        <td style={{ ...fs.td, textAlign: "right", whiteSpace: "nowrap" }}>{r.diff_steuer !== null ? geld(r.diff_steuer, true) : r.diff_bmg !== null ? `${zahl(r.diff_bmg)} €` : ""}</td>
                        <td style={{ ...fs.td, whiteSpace: "nowrap" }}><Chip text={V_STATUS[r.status][0]} farbe={V_STATUS[r.status][1]} /></td>
                        <td style={fs.td}><button style={{ ...fs.btnGray, padding: "4px 10px" }} onClick={() => setDetail(r)}>Buchungen</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {v.ursachen_kz81 && <Ursachen u={v.ursachen_kz81} />}
          <Klein>{v.freigabefaehig ? "Die ComeFit-UStVA ist technisch freigabefähig (keine Bestätigung der Richtigkeit)." : `Die ComeFit-UStVA ist NICHT freigabefähig (${v.blocker} Blocker) – siehe Seite „Umsatzsteuer-Voranmeldung“.`}</Klein>
        </>
      )}
      {detail && <Detail wahl={wahl} kz={detail.kz} text={detail.text} onSchliessen={() => setDetail(null)} />}
      <ProtokollQualitaet />
    </div>
  );
}

const U_STATUS = { hypothese: ["Hypothese", THEME.colors.warning], kanzlei_sonderbuchung: ["Kanzlei-Sonderbuchung", THEME.colors.accent], vermutung: ["Vermutung", THEME.colors.warning], nachgewiesen: ["nachgewiesen", THEME.colors.success], offen: ["offen", THEME.colors.danger] };

function Ursachen({ u }) {
  return (
    <Abschnitt titel={`Ursachen der Abweichung bei Kz 81 (Differenz Kanzlei − ComeFit: ${geld(u.differenz_steuer)})`}>
      <Klein>{u.hinweis}</Klein>
      <div style={{ marginTop: 8 }}>
        {u.positionen.map((p, i) => {
          const [t, f] = U_STATUS[p.status] || [p.status, THEME.colors.textMuted];
          return (
            <div key={i} style={{ ...fs.karte, padding: "8px 12px", marginBottom: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}><strong style={{ overflowWrap: "anywhere" }}>{p.titel}</strong><span><Chip text={t} farbe={f} /> {p.betrag !== null && p.betrag !== undefined && <strong>{geld(p.betrag)}</strong>}</span></div>
              <Klein>{p.text}</Klein>
            </div>
          );
        })}
      </div>
      <Klein>Erklärt (rechnerisch, einschließlich Hypothesen): {geld(u.erklaert_summe)} von {geld(u.differenz_steuer)}.</Klein>
    </Abschnitt>
  );
}

function ProtokollQualitaet() {
  const [liste, setListe] = useState(null);
  useEffect(() => { pbApi.ustvaProtokolle().then(setListe).catch(() => setListe([])); }, []);
  if (!liste || !liste.length) return null;
  return (
    <Abschnitt titel="Datenqualität der importierten UStVA-Protokolle der Kanzlei">
      {liste.map(p => (
        <div key={p.id} style={{ ...fs.karte, padding: "8px 12px", marginBottom: 6 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <strong>{String(p.monat).padStart(2, "0")}/{p.jahr}</strong>
            <Chip text={p.status === "ok" ? "in Ordnung" : p.status === "warnung" ? "Hinweise" : "FEHLER"} farbe={p.status === "ok" ? THEME.colors.success : p.status === "warnung" ? THEME.colors.warning : THEME.colors.danger} />
            <Klein>{p.dateiname}</Klein>
          </div>
          {p.pruefungen.map((c, i) => <Klein key={i} farbe={c.ok ? undefined : THEME.colors.warning}>{c.ok ? "✓" : "⚠"} {c.text}</Klein>)}
        </div>
      ))}
    </Abschnitt>
  );
}

function VergleichKarte({ r, onDetail }) {
  const [t, f] = V_STATUS[r.status];
  return (
    <div style={{ ...fs.karte, padding: "10px 12px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><strong>Kz {r.kz}</strong><Chip text={t} farbe={f} /></div>
      <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{r.text}</div>
      <div style={{ marginTop: 4, fontSize: THEME.fontSize.sm }}>
        {(r.comefit_bmg !== null || r.kanzlei_bmg !== null) && <div>BMG: ComeFit {r.comefit_bmg === null ? "–" : zahl(r.comefit_bmg)} € · Kanzlei {r.kanzlei_bmg === null ? "–" : zahl(r.kanzlei_bmg)} €</div>}
        {(r.comefit_steuer !== null || r.kanzlei_steuer !== null) && <div>Steuer: ComeFit {geld(r.comefit_steuer)} · Kanzlei {geld(r.kanzlei_steuer)}{r.diff_steuer !== null && <strong> · Differenz {geld(r.diff_steuer, true)}</strong>}</div>}
      </div>
      {r.hinweis && <Klein>{r.hinweis}</Klein>}
      <button style={{ ...fs.btnGray, padding: "6px 12px", marginTop: 6 }} onClick={onDetail}>Buchungen</button>
    </div>
  );
}
